/**
 * Dashboard Hooks
 *
 * React Query hooks for fetching dashboard data
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { dashboardService } from '@/services/dashboardService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import type {
  DashboardResponse,
  DashboardStat,
  FinanceWidget,
  ChildAbsence,
} from '@/types/dashboard.types';

const STORAGE_KEY = 'dashboard_previous_values';

/**
 * Get stored previous values from localStorage
 */
function getPreviousValues(): Record<string, string | number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Store current values to localStorage
 */
function storeCurrentValues(stats: DashboardStat[]): void {
  try {
    const values: Record<string, string | number> = {};
    stats.forEach((stat) => {
      values[stat.id] = stat.value;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook for fetching role-based dashboard stats
 * Automatically uses the correct dashboard based on user role
 * Tracks value changes and marks stats that changed since last load
 */
export function useDashboardStats(academicYearId?: string) {
  // Track if this is the first load in this session
  const isFirstLoad = useRef(true);
  const previousValuesChecked = useRef(false);
  // Key by user so an account switch never serves the previous user's stats
  const userId = useAuthStore((state) => state.user?.id);

  const query = useQuery<DashboardResponse>({
    // The year is part of the key: two years must never share a cache entry.
    queryKey: ['dashboard', 'stats', userId, academicYearId],
    queryFn: () => dashboardService.getDashboardStats(academicYearId),
    staleTime: 60000, // 1 minute cache
    refetchOnWindowFocus: false, // Only on page load
    select: useCallback((data: DashboardResponse): DashboardResponse => {
      // Only check for changes on first load
      if (!isFirstLoad.current || previousValuesChecked.current) {
        return data;
      }

      const previousValues = getPreviousValues();
      const hasAnyPrevious = Object.keys(previousValues).length > 0;

      // Mark stats that have changed
      const statsWithChanges = data.stats.map((stat) => {
        const prevValue = previousValues[stat.id];
        const hasChanged = hasAnyPrevious && prevValue !== undefined && prevValue !== stat.value;
        return { ...stat, hasChanged };
      });

      // Store current values for next comparison
      storeCurrentValues(data.stats);
      previousValuesChecked.current = true;

      return {
        ...data,
        stats: statsWithChanges,
      };
    }, []),
  });

  // Reset first load flag when component unmounts
  // (so next mount will check for changes again)

  return query;
}

/**
 * Hook for fetching student finance widget data
 */
export function useStudentFinanceWidget() {
  const userId = useAuthStore((state) => state.user?.id);
  return useQuery<FinanceWidget | null>({
    queryKey: ['dashboard', 'finance', userId],
    queryFn: dashboardService.getStudentFinanceWidget,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching child absences (parent dashboard)
 */
export function useChildAbsences() {
  const userId = useAuthStore((state) => state.user?.id);
  return useQuery<ChildAbsence[]>({
    queryKey: ['dashboard', 'child-absences', userId],
    queryFn: dashboardService.getChildAbsences,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}
